import React, { useState, useEffect, useMemo } from 'react'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Checkbox } from 'components/ui/checkbox'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Select } from 'components/ui/select'
import { GripVertical } from 'lucide-react'

const PRIORITY_OPTIONS = [
	{ value: 'P1 - Urgent', label: 'P1 - Urgent' },
	{ value: 'P2 - High', label: 'P2 - High' },
	{ value: 'P3 - Medium', label: 'P3 - Medium' },
	{ value: 'P4 - Low', label: 'P4 - Low' },
]

const EFFORT_OPTIONS = [
	{ value: 'XS - Trivial', label: 'XS - Trivial' },
	{ value: 'S - Small', label: 'S - Small' },
	{ value: 'M - Medium', label: 'M - Medium' },
	{ value: 'L - Large', label: 'L - Large' },
	{ value: 'XL - Epic', label: 'XL - Epic' },
]

interface TaskItem {
	_id: string
	taskName: string
	taskPriority?: string
	taskEffort?: string
}

interface BuildTemplateModalProps {
	tasks: TaskItem[]
	openPopup: boolean
	setOpenPopup: (open: boolean) => void
	onBuildTemplate: (data: any) => void
}

interface SortableRowProps {
	id: string
	seq: number
	task: TaskItem
	isSelected: boolean
	isDisabled: boolean
	priority: string
	effort: string
	onSelect: (id: string, checked: boolean) => void
	onPriorityChange: (id: string, value: string) => void
	onEffortChange: (id: string, value: string) => void
}

const SortableRow = ({
	id,
	seq,
	task,
	isSelected,
	isDisabled,
	priority,
	effort,
	onSelect,
	onPriorityChange,
	onEffortChange,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_1fr_130px_120px_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			{/* Drag Handle */}
			<div
				{...attributes}
				{...listeners}
				className={`flex items-center justify-center cursor-grab active:cursor-grabbing ${
					isDisabled ? 'opacity-30 pointer-events-none' : 'text-gray-400 hover:text-gray-600'
				}`}
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			{/* Seq */}
			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			{/* Task Name */}
			<div className="text-sm text-gray-900 truncate" title={task.taskName}>
				{task.taskName}
			</div>

			{/* Priority */}
			<Select
				value={priority}
				onChange={e => onPriorityChange(id, e.target.value)}
				disabled={isDisabled}
				className="h-7 text-xs"
			>
				{PRIORITY_OPTIONS.map(opt => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</Select>

			{/* Effort */}
			<Select
				value={effort}
				onChange={e => onEffortChange(id, e.target.value)}
				disabled={isDisabled}
				className="h-7 text-xs"
			>
				{EFFORT_OPTIONS.map(opt => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</Select>

			{/* Select Checkbox */}
			<div className="flex items-center justify-center">
				<Checkbox checked={isSelected} onCheckedChange={checked => onSelect(id, !!checked)} disabled={isDisabled} />
			</div>
		</div>
	)
}

const BuildTemplateModal = ({ tasks, openPopup, setOpenPopup, onBuildTemplate }: BuildTemplateModalProps) => {
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set())
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [taskOrder, setTaskOrder] = useState<string[]>([])
	const [taskOverrides, setTaskOverrides] = useState<Record<string, { priority: string; effort: string }>>({})

	// Build lookup map
	const tasksById = useMemo(() => {
		const map: Record<string, TaskItem> = {}
		;(tasks || []).forEach(t => {
			map[t._id] = t
		})
		return map
	}, [tasks])

	// Initialize task order and overrides when tasks change
	useEffect(() => {
		const ids = (tasks || []).map(t => t._id)
		setTaskOrder(ids)
		const overrides: Record<string, { priority: string; effort: string }> = {}
		;(tasks || []).forEach(t => {
			overrides[t._id] = {
				priority: t.taskPriority || 'P3 - Medium',
				effort: t.taskEffort || 'M - Medium',
			}
		})
		setTaskOverrides(overrides)
	}, [tasks])

	// DnD sensors
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setTaskOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
		}
	}

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedTasks(new Set(taskOrder))
		} else {
			setSelectedTasks(new Set())
		}
	}

	const handleTaskSelect = (taskId: string, checked: boolean) => {
		const newSelected = new Set(selectedTasks)
		if (checked) {
			newSelected.add(taskId)
		} else {
			newSelected.delete(taskId)
		}
		setSelectedTasks(newSelected)
	}

	const handlePriorityChange = (taskId: string, value: string) => {
		setTaskOverrides(prev => ({
			...prev,
			[taskId]: { ...prev[taskId], priority: value },
		}))
	}

	const handleEffortChange = (taskId: string, value: string) => {
		setTaskOverrides(prev => ({
			...prev,
			[taskId]: { ...prev[taskId], effort: value },
		}))
	}

	const isAllSelected = taskOrder.length > 0 && taskOrder.every(id => selectedTasks.has(id))
	const isIndeterminate = !isAllSelected && taskOrder.some(id => selectedTasks.has(id))

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) {
			newErrors.templateName = 'Template name is required'
		}
		if (!description.trim()) {
			newErrors.description = 'Description is required'
		}
		if (selectedTasks.size === 0) {
			newErrors.tasks = 'At least one task must be selected'
		}
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleBuildTemplate = () => {
		if (!validate()) return

		const orderedSelectedTasks = taskOrder
			.filter(id => selectedTasks.has(id))
			.map((id, index) => {
				const task = tasksById[id]
				const overrides = taskOverrides[id]
				return {
					_id: id,
					taskName: task.taskName,
					taskPriority: overrides?.priority || task.taskPriority || 'P3 - Medium',
					taskEffort: overrides?.effort || task.taskEffort || 'M - Medium',
					seq: index + 1,
				}
			})

		onBuildTemplate({
			templateName: templateName.trim(),
			description: description.trim(),
			accessLevel,
			selectedTasks: orderedSelectedTasks,
			selectedTaskIds: orderedSelectedTasks.map(t => t._id),
		})
	}

	const isFieldsComplete = templateName.trim() && description.trim()
	const isListDisabled = !isFieldsComplete
	const isBuildDisabled = !isFieldsComplete || selectedTasks.size === 0

	// Reset form when modal closes
	useEffect(() => {
		if (!openPopup) {
			setTemplateName('')
			setDescription('')
			setSelectedTasks(new Set())
			setAccessLevel('private')
			setErrors({})
		}
	}, [openPopup])

	return (
		<div className="p-3 min-w-[37.5rem] max-w-[50rem] max-h-[46.875rem] flex flex-col gap-2">
			{/* Template Name */}
			<div className="flex flex-col gap-1">
				<div className="mb-1">
					<label htmlFor="templateName" className="block text-xs font-medium text-gray-700 mb-1">
						Template Name <span className="text-red-500">*</span>
					</label>
					<Input
						id="templateName"
						type="text"
						value={templateName}
						onChange={e => {
							setTemplateName(e.target.value)
							if (errors.templateName) setErrors({ ...errors, templateName: '' })
						}}
						className={`text-xs h-8 ${errors.templateName ? 'border-red-500' : ''}`}
						placeholder="Template Name"
					/>
					{errors.templateName && <p className="text-red-500 text-[0.7rem] mt-0.5">{errors.templateName}</p>}
				</div>
				<div className="mb-1">
					<label htmlFor="templateDescription" className="block text-xs font-medium text-gray-700 mb-1">
						Description <span className="text-red-500">*</span>
					</label>
					<Textarea
						id="templateDescription"
						value={description}
						onChange={e => {
							setDescription(e.target.value)
							if (errors.description) setErrors({ ...errors, description: '' })
						}}
						className={`text-xs min-h-[3.5rem] ${errors.description ? 'border-red-500' : ''}`}
						placeholder="Describe the purpose of this template"
						rows={2}
					/>
					{errors.description && <p className="text-red-500 text-[0.7rem] mt-0.5">{errors.description}</p>}
				</div>
			</div>

			{/* Task List */}
			<div className="flex-1 overflow-y-auto border border-gray-300 rounded min-h-[21.875rem]">
				{/* Header */}
				<div className="grid grid-cols-[24px_32px_1fr_130px_120px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-300 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
					<span></span>
					<span className="text-center">Seq</span>
					<span>Task Name</span>
					<span>Priority</span>
					<span>Effort</span>
					<label className="flex items-center justify-center cursor-pointer">
						<Checkbox
							checked={isAllSelected}
							onCheckedChange={checked => handleSelectAll(!!checked)}
							disabled={isListDisabled}
						/>
					</label>
				</div>

				<div>
					{taskOrder.length === 0 ? (
						<div className="p-4 text-center text-gray-500 text-sm">No tasks available to build template from</div>
					) : (
						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
							<SortableContext items={taskOrder} strategy={verticalListSortingStrategy}>
								{taskOrder.map((id, index) => {
									const task = tasksById[id]
									if (!task) return null
									return (
										<SortableRow
											key={id}
											id={id}
											seq={index + 1}
											task={task}
											isSelected={selectedTasks.has(id)}
											isDisabled={isListDisabled}
											priority={taskOverrides[id]?.priority || 'P3 - Medium'}
											effort={taskOverrides[id]?.effort || 'M - Medium'}
											onSelect={handleTaskSelect}
											onPriorityChange={handlePriorityChange}
											onEffortChange={handleEffortChange}
										/>
									)
								})}
							</SortableContext>
						</DndContext>
					)}
				</div>
				{errors.tasks && <p className="text-red-500 text-[0.7rem] mt-0.5 px-3">{errors.tasks}</p>}
			</div>

			{/* Access Level */}
			<div className="flex flex-col gap-1 p-2 bg-gray-50 rounded">
				<div className="font-semibold text-xs mb-0.5">Template Access Level</div>
				<div className="flex flex-row gap-4">
					<label className="flex items-center gap-1.5 cursor-pointer text-xs">
						<input
							type="radio"
							name="accessLevel"
							value="private"
							checked={accessLevel === 'private'}
							onChange={e => setAccessLevel(e.target.value)}
							className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
						/>
						Private
					</label>
					<label className="flex items-center gap-1.5 cursor-pointer text-xs">
						<input
							type="radio"
							name="accessLevel"
							value="org-wide"
							checked={accessLevel === 'org-wide'}
							onChange={e => setAccessLevel(e.target.value)}
							className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
						/>
						Org-Wide
					</label>
					<label className="flex items-center gap-1.5 cursor-pointer text-xs">
						<input
							type="radio"
							name="accessLevel"
							value="public"
							checked={accessLevel === 'public'}
							onChange={e => setAccessLevel(e.target.value)}
							className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
						/>
						Public
					</label>
				</div>
				<div className="text-[0.7rem] text-gray-600 mt-0.5">
					{accessLevel === 'private' && 'Only you can see and use this template.'}
					{accessLevel === 'org-wide' && 'All users in your company can see and use this template.'}
					{accessLevel === 'public' && 'All users in the system can see and use this template.'}
				</div>
			</div>

			{/* Actions */}
			<div className="flex flex-col gap-1.5 pt-1 border-t border-gray-300">
				<Button onClick={handleBuildTemplate} disabled={isBuildDisabled} className="w-full">
					Tasks Template
				</Button>

				<div className="p-2 bg-blue-50 border-l-[3px] border-blue-500 rounded text-[0.7rem] text-gray-700 leading-relaxed">
					<strong>Tip:</strong> Select the tasks you want to include in your template. You can drag rows to reorder them
					and adjust priority and effort levels. Once created, this template will be available in your task templates
					list.
				</div>
			</div>
		</div>
	)
}

export default BuildTemplateModal
